import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import {Director, Shareholder} from "@/services/accountService/companyAnnualService";

/**
 * ✅ 简化版：只包含年度信息，不包含 line
 * line 数据应该由父组件（DialogLauncher）通过 payload.line 传递
 */
export type AnnualMeta = {
    periodFrom: string | null;
    periodTo: string | null;
    yearOfAssessment: string | null;
    lastYearOfAssessment: string | null;
    currency: string;
};

/**
 * Fetch Year of Assessment, Last Year of Assessment and Presentation Currency
 * for a given annualId using company annual store getters.
 * 
 * ⚠️ 重要变更：不再获取 CategoryLine
 * - CategoryLine 应该从 payload.line 获取（由 DialogLauncher 传递）
 * - 这样可以避免异步时序问题，确保数据同步
 * 
 * @param annualId - 年度ID
 * @returns 年度元数据（不包含 line）
 */
export async function fetchAnnualMeta(annualId: number): Promise<AnnualMeta> {
    try {
        const state = useCompanyAnnualStore.getState();
        const [yoaRes, currencyRes] = await Promise.all([
            state.getYearOfAssessment(annualId),
            state.getPresentationCurrency(annualId),
        ]);

        return {
            periodFrom: yoaRes.periodFrom ?? null,
            periodTo: yoaRes?.periodTo ?? null,
            yearOfAssessment: yoaRes?.yearOfAssessment ?? null,
            lastYearOfAssessment: yoaRes?.lastYearOfAssessment ?? null,
            currency: String(currencyRes || ''),
        };
    } catch {
        return {
            periodFrom: null,
            periodTo: null,
            yearOfAssessment: null,
            lastYearOfAssessment: null,
            currency: '',
        };
    }
}

export type CoIssuedCapitalMeta = {
    registeredOffice: string | null;
    currentRegisteredOffice: string | null;
    changeOfAddress: number | null;
    lastAddress: string | null;
    changeDate: string | null;
    directors: Director[]
    shareholders: Shareholder[]
    holdingCo: number | null;
    nameOfImmediate: string | null;
    immediateHolding: string | null;
    nameOfUltimate: string | null;
    ultimateHolding: string | null;
    annualMeta: AnnualMeta
}

/**
 * Fetch company issued capital metadata including annual meta
 * @param annualId - 年度ID
 */
export async function fetchCoIssuedCapitalMeta(annualId: number): Promise<CoIssuedCapitalMeta> {
    try {
        const state = useCompanyAnnualStore.getState();
        const [officeInfo, directors, shareholders, holdingCo] = await Promise.all([
            state.getRegisteredOffice(annualId),
            state.getDirectorsInfo(annualId),
            state.getShareholdersInfo(annualId),
            state.getHoldingStructureInfo(annualId),
        ]);

        const meta = await fetchAnnualMeta(annualId);

        return {
            registeredOffice: officeInfo?.registeredOffice ?? null,
            currentRegisteredOffice: officeInfo?.currentRegisteredOffice ?? officeInfo?.registeredOffice ?? null,
            changeOfAddress: officeInfo?.changeOfAddress ?? null,
            lastAddress: officeInfo?.lastAddress ?? null,
            changeDate: officeInfo?.changeDate ?? null,

            directors: directors ?? [],
            shareholders: shareholders ?? [],

            holdingCo: holdingCo?.holdingCo ?? null,
            nameOfImmediate: holdingCo?.nameOfImmediate ?? null,
            immediateHolding: holdingCo?.immediateHolding ?? null,
            nameOfUltimate: holdingCo?.nameOfUltimate ?? null,
            ultimateHolding: holdingCo?.ultimateHolding ?? null,

            annualMeta: meta,

        };
    } catch {
        // Fallback with safe defaults across the full CoIssuedCapitalMeta shape
        return {
            registeredOffice: null,
            currentRegisteredOffice: null,
            changeOfAddress: null,
            lastAddress: null,
            changeDate: null,
            directors: [],
            shareholders: [],
            holdingCo: null,
            nameOfImmediate: null,
            immediateHolding: null,
            nameOfUltimate: null,
            ultimateHolding: null,
            annualMeta: {
                periodFrom: null,
                periodTo: null,
                yearOfAssessment: null,
                lastYearOfAssessment: null,
                currency: '',
            },
        };
    }
}
